// Module: slideCaptureSvgStyles
// -----------------------------------------------------------------
// Receives:  a function that runs html2canvas's synchronous clone
// Produces:  the same clone, with SVG elements carrying only paint styles
//            and every other element styled as html2canvas would
// Owns:      the list of SVG paint properties
// Does NOT:  touch the live DOM or anything outside that call (→ slideScreenshot)
// -----------------------------------------------------------------
//
// html2canvas copies all ~520 computed properties into each cloned SVG element
// (76 of them took ~1.4s on an iPad). Only these affect how SVG paints, or
// where a root <svg> sits in the HTML around it (e.g. KaTeX's \sqrt). The same
// copy styles ::before/::after content, which keeps its full copy.

export const SVG_PAINT_PROPERTIES: ReadonlySet<string> = new Set([
  "fill",
  "fill-opacity",
  "fill-rule",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-miterlimit",
  "marker-start",
  "marker-mid",
  "marker-end",
  "opacity",
  "visibility",
  "display",
  "color",
  "overflow",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "font-stretch",
  "font-variant",
  "letter-spacing",
  "word-spacing",
  "white-space",
  "text-anchor",
  "dominant-baseline",
  "alignment-baseline",
  "baseline-shift",
  "text-decoration",
  "text-decoration-line",
  "text-decoration-color",
  "transform",
  "transform-origin",
  "transform-box",
  "clip-path",
  "clip-rule",
  "mask",
  "filter",
  "paint-order",
  "vector-effect",
  "stop-color",
  "stop-opacity",
  "flood-color",
  "flood-opacity",
  "lighting-color",
  "shape-rendering",
  "text-rendering",
  "color-interpolation",
  "color-interpolation-filters",
  "mix-blend-mode",
  "isolation",
  "width",
  "height",
  "position",
  "top",
  "right",
  "bottom",
  "left",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "vertical-align",
  "z-index",
  "x",
  "y",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
]);

/**
 * Runs `cloneSync` with the inline styles of cloned SVG elements limited to
 * SVG_PAINT_PROPERTIES. Every other setProperty goes through untouched.
 *
 * Requires: `cloneSync` is html2canvas's synchronous clone, which clones SVG
 *           elements with cloneNode and then fills their inline style.
 * Ensures:  cloneNode and setProperty are restored before this returns or throws.
 */
export function withSvgStyleCopyLimited<T>(cloneSync: () => T): T {
  const nodeProto = Node.prototype;
  const styleProto = CSSStyleDeclaration.prototype;
  const originalCloneNode = nodeProto.cloneNode;
  const originalSetProperty = styleProto.setProperty;
  // element.style is the same object on every read, so it identifies the clone.
  const svgCloneStyles = new WeakSet<CSSStyleDeclaration>();

  nodeProto.cloneNode = function (deep?: boolean) {
    const clone = originalCloneNode.call(this, deep);
    if (this instanceof SVGElement && clone instanceof SVGElement) {
      svgCloneStyles.add(clone.style);
    }
    return clone;
  };
  styleProto.setProperty = function (property, ...rest) {
    if (svgCloneStyles.has(this) && !SVG_PAINT_PROPERTIES.has(property)) {
      return;
    }
    originalSetProperty.call(this, property, ...rest);
  };
  try {
    return cloneSync();
  } finally {
    nodeProto.cloneNode = originalCloneNode;
    styleProto.setProperty = originalSetProperty;
  }
}
